export type LetterStarMotion = 'quiet' | 'standard' | 'rich';

export interface LetterAnimationSettings {
  starCount: number;
  starMotion: LetterStarMotion;
  starlightStarTwinkle: boolean;
  /** 首页照片球是否显示经纬骨架。 */
  photoSphereSkeleton: boolean;
  /** 星空主题背景星点密度，所有可调动效设置统一由动效管理持有。 */
  starlightDensity: number;
  /** 星空星点呼吸频率；与密度独立。 */
  starlightTwinkleSpeed: number;
  /** 时间相册最多显示的集锦层数（含前层）。 */
  albumStackLayers: number;
  /** 时间相册显示的后层数量。 */
  albumBackLayers: number;
  /** 时间相册顶部露出系数，100 表示基准值。 */
  albumTopReveal: number;
}

export const DEFAULT_LETTER_ANIMATION_SETTINGS: LetterAnimationSettings = {
  starCount: 24,
  starMotion: 'rich',
  starlightStarTwinkle: true,
  photoSphereSkeleton: true,
  starlightDensity: 55,
  starlightTwinkleSpeed: 50,
  albumStackLayers: 4,
  albumBackLayers: 3,
  albumTopReveal: 100,
};

const STORAGE_KEY = 'prologue.animation-settings.letter';
export const ANIMATION_SETTINGS_CHANGE_EVENT = 'prologue:animation-settings-change';
const MIN_STAR_COUNT = 8;
const MAX_STAR_COUNT = 60;
const MIN_ALBUM_STACK_LAYERS = 1;
const MAX_ALBUM_STACK_LAYERS = 5;
const MIN_ALBUM_BACK_LAYERS = 0;
const MAX_ALBUM_BACK_LAYERS = 4;
const MIN_ALBUM_TOP_REVEAL = 0;
const MAX_ALBUM_TOP_REVEAL = 200;

export const STAR_DENSITY_STORAGE_KEY = 'prologue.animation-settings.starlight-density';
export const DEFAULT_STAR_DENSITY = DEFAULT_LETTER_ANIMATION_SETTINGS.starlightDensity;
export const DEFAULT_STAR_TWINKLE_SPEED = DEFAULT_LETTER_ANIMATION_SETTINGS.starlightTwinkleSpeed;

function clampStarCount(value: unknown): number {
  const count = typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : DEFAULT_LETTER_ANIMATION_SETTINGS.starCount;
  return Math.min(MAX_STAR_COUNT, Math.max(MIN_STAR_COUNT, count));
}

function isStarMotion(value: unknown): value is LetterStarMotion {
  return value === 'quiet' || value === 'standard' || value === 'rich';
}

function clampStarlightDensity(value: unknown): number {
  const density = typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : DEFAULT_STAR_DENSITY;
  return Math.min(100, Math.max(0, density));
}

function clampTwinkleSpeed(value: unknown): number {
  const speed = typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : DEFAULT_STAR_TWINKLE_SPEED;
  return Math.min(100, Math.max(0, speed));
}

function clampAlbumStackLayers(value: unknown): number {
  const layers = typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : DEFAULT_LETTER_ANIMATION_SETTINGS.albumStackLayers;
  return Math.min(MAX_ALBUM_STACK_LAYERS, Math.max(MIN_ALBUM_STACK_LAYERS, layers));
}

function clampAlbumBackLayers(value: unknown): number {
  const layers = typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : DEFAULT_LETTER_ANIMATION_SETTINGS.albumBackLayers;
  return Math.min(MAX_ALBUM_BACK_LAYERS, Math.max(MIN_ALBUM_BACK_LAYERS, layers));
}

function clampAlbumTopReveal(value: unknown): number {
  const reveal = typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : DEFAULT_LETTER_ANIMATION_SETTINGS.albumTopReveal;
  return Math.min(MAX_ALBUM_TOP_REVEAL, Math.max(MIN_ALBUM_TOP_REVEAL, reveal));
}

export function loadLetterAnimationSettings(): LetterAnimationSettings {
  if (typeof window === 'undefined') return DEFAULT_LETTER_ANIMATION_SETTINGS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_LETTER_ANIMATION_SETTINGS;
    const parsed = JSON.parse(raw) as {
      starCount?: unknown;
      starMotion?: unknown;
      starlightStarTwinkle?: unknown;
      photoSphereSkeleton?: unknown;
      starlightDensity?: unknown;
      starlightTwinkleSpeed?: unknown;
      albumStackLayers?: unknown;
      albumBackLayers?: unknown;
      albumTopReveal?: unknown;
    };
    const legacyDensity = window.localStorage.getItem('prologue.theme.starlight-density');
    return {
      starCount: clampStarCount(parsed.starCount),
      starMotion: isStarMotion(parsed.starMotion)
        ? parsed.starMotion
        : DEFAULT_LETTER_ANIMATION_SETTINGS.starMotion,
      starlightStarTwinkle: typeof parsed.starlightStarTwinkle === 'boolean'
        ? parsed.starlightStarTwinkle
        : DEFAULT_LETTER_ANIMATION_SETTINGS.starlightStarTwinkle,
      photoSphereSkeleton: typeof parsed.photoSphereSkeleton === 'boolean'
        ? parsed.photoSphereSkeleton
        : DEFAULT_LETTER_ANIMATION_SETTINGS.photoSphereSkeleton,
      starlightDensity: clampStarlightDensity(parsed.starlightDensity ?? (legacyDensity === null ? undefined : Number(legacyDensity))),
      starlightTwinkleSpeed: clampTwinkleSpeed(parsed.starlightTwinkleSpeed),
      albumStackLayers: clampAlbumStackLayers(parsed.albumStackLayers),
      albumBackLayers: clampAlbumBackLayers(parsed.albumBackLayers),
      albumTopReveal: clampAlbumTopReveal(parsed.albumTopReveal),
    };
  } catch {
    return DEFAULT_LETTER_ANIMATION_SETTINGS;
  }
}

export function saveLetterAnimationSettings(settings: LetterAnimationSettings): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
      starCount: clampStarCount(settings.starCount),
      starMotion: isStarMotion(settings.starMotion)
        ? settings.starMotion
        : DEFAULT_LETTER_ANIMATION_SETTINGS.starMotion,
      starlightStarTwinkle: settings.starlightStarTwinkle
        ?? DEFAULT_LETTER_ANIMATION_SETTINGS.starlightStarTwinkle,
      photoSphereSkeleton: settings.photoSphereSkeleton
        ?? DEFAULT_LETTER_ANIMATION_SETTINGS.photoSphereSkeleton,
      starlightDensity: clampStarlightDensity(settings.starlightDensity),
      starlightTwinkleSpeed: clampTwinkleSpeed(settings.starlightTwinkleSpeed),
      albumStackLayers: clampAlbumStackLayers(settings.albumStackLayers),
      albumBackLayers: clampAlbumBackLayers(settings.albumBackLayers),
      albumTopReveal: clampAlbumTopReveal(settings.albumTopReveal),
    }));
    window.dispatchEvent(new Event(ANIMATION_SETTINGS_CHANGE_EVENT));
  } catch {
    // 无法使用本地存储时保留当前页内状态，不影响动效使用。
  }
}
